import { createContext, useContext, useMemo } from "react";
import { DEFAULT_WIDGET_THEME, normaliseWidgetTheme, type WidgetTheme } from "../../utils/widgetTheme";

/**
 * The design a surface paints. A union rather than a boolean so a flow can be
 * migrated, and if necessary held back, without a second flag.
 */
export type DesignSystem = "legacy" | "current";

export interface GuestCheckoutSettings {
  /** Hide the offer selector in the customer details step. */
  hideOffers: boolean;
  /** Enable searching within the venue list (currently accepted for parity with the legacy booking form). */
  searchVenues: boolean;
  /** Show the booking type selector before the date/covers step. */
  showTypeFirst: boolean;
  /** Automatically maximise the widget when a pre-order booking reaches Your Details. Widget-only. */
  autoExpandForPreorder: boolean;
  /** Locale code used for i18n and API calls (e.g. "en-GB", "sv"). Full app only: the widget formats with the browser locale. */
  locale?: string;
  /** Comma-separated venue IDs provided via the web component attribute for multi-venue selection. */
  venues?: string;
  /** Comma-separated booking type IDs to restrict the type selector to a subset of the venue's types. */
  allowedTypes?: string;
  /** Rendering inside the `<booking-form>` widget rather than the full Guest Checkout app. */
  isWidget: boolean;
  /**
   * Which design the surface paints. `current` is the design the widget
   * introduced, now adopted flow by flow; `legacy` is the original Guest
   * Checkout look, still the default for anything not yet migrated.
   *
   * Distinct from `isWidget`, which also changes the *markup* of the shared
   * components (the confirmation cards and headers): a surface can take the
   * design while keeping the shared layout. Implied `current` by `isWidget`.
   */
  design: DesignSystem;
  /**
   * Changes whenever the venue's applied `--gc-*` overrides do. Derived by
   * useThemedSurface, not an attribute: the surfaces repaint from the variables
   * themselves, but the hosted payment fields are styled from their *computed*
   * values and have to be told to read again.
   */
  venueStyleKey: string;
  /**
   * Colour scheme for the widget: `auto` follows the browser preference,
   * `light`/`dark` force it. Set from the `<booking-form>` `theme` attribute
   * or the `theme` query parameter; no rendering consumes it yet.
   */
  theme: WidgetTheme;
}

const defaultSettings: GuestCheckoutSettings = {
  hideOffers: false,
  searchVenues: false,
  showTypeFirst: false,
  autoExpandForPreorder: true,
  isWidget: false,
  design: "legacy",
  venueStyleKey: "",
  theme: DEFAULT_WIDGET_THEME,
};

const GuestCheckoutSettingsContext = createContext<GuestCheckoutSettings | undefined>(undefined);

function normaliseBoolean(value: string | null | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === null || value === "undefined" || value === "null" || value === "") {
    return defaultValue;
  }
  return value !== "false";
}

function normaliseDesign(value: string | null | undefined): DesignSystem {
  return value === "current" ? "current" : "legacy";
}

function normaliseLocale(value: string | null | undefined): string | undefined {
  if (value === undefined || value === null || value === "undefined" || value === "null" || value === "") {
    return undefined;
  }
  return value;
}

export function mergeGuestCheckoutSettings(
  input: Partial<Record<keyof GuestCheckoutSettings, string | undefined>>,
): GuestCheckoutSettings {
  const isWidget = normaliseBoolean(input.isWidget, defaultSettings.isWidget);
  const locale = normaliseLocale(input.locale);

  return {
    hideOffers: normaliseBoolean(input.hideOffers, defaultSettings.hideOffers),
    searchVenues: normaliseBoolean(input.searchVenues, defaultSettings.searchVenues),
    showTypeFirst: normaliseBoolean(input.showTypeFirst, defaultSettings.showTypeFirst),
    autoExpandForPreorder: normaliseBoolean(input.autoExpandForPreorder, defaultSettings.autoExpandForPreorder),
    locale,
    venues: normaliseLocale(input.venues),
    allowedTypes: normaliseLocale(input.allowedTypes),
    isWidget,
    // The widget is where the design came from, so it always paints it.
    design: isWidget ? "current" : normaliseDesign(input.design),
    venueStyleKey: input.venueStyleKey ?? defaultSettings.venueStyleKey,
    theme: normaliseWidgetTheme(input.theme),
  };
}

export function useGuestCheckoutSettings(): GuestCheckoutSettings {
  return useContext(GuestCheckoutSettingsContext) ?? defaultSettings;
}

/**
 * Whether the surface paints the design the widget introduced, as opposed to
 * the legacy Guest Checkout look.
 *
 * The lever for anything that differs *by design* — type, spacing, which
 * ornament is shown. `isWidget` is the lever for what differs because the
 * surface is the widget specifically (its own copy, its own chrome): a
 * migrated flow takes the design while keeping the shared layout, so the two
 * are not interchangeable.
 */
export function useUsesDesign(): boolean {
  return useGuestCheckoutSettings().design === "current";
}

interface GuestCheckoutSettingsProviderProps {
  readonly value: GuestCheckoutSettings;
  readonly children: React.ReactNode;
}

export function GuestCheckoutSettingsProvider({ value, children }: GuestCheckoutSettingsProviderProps) {
  const settings = useMemo(() => value, [value]);
  return <GuestCheckoutSettingsContext.Provider value={settings}>{children}</GuestCheckoutSettingsContext.Provider>;
}
